import {
  registerDecorator,
  ValidationOptions,
  ValidatorConstraint,
  ValidatorConstraintInterface,
} from 'class-validator';

// 16 MiB in bytes = 16 * 1024 * 1024 = 16,777,216 bytes
export const MAX_CONTENT_SIZE_BYTES = 16 * 1024 * 1024;

@ValidatorConstraint({ async: false })
export class MaxContentSizeConstraint implements ValidatorConstraintInterface {
  validate(content: string) {
    if (!content) {
      return true; // Allow empty content regardless of post status
    }

    // Calculate the actual byte size of the content using cross-platform helper
    const sizeInBytes = getContentSizeInBytes(content);
    return sizeInBytes <= MAX_CONTENT_SIZE_BYTES;
  }

  defaultMessage() {
    const maxSizeInMiB = (MAX_CONTENT_SIZE_BYTES / (1024 * 1024)).toFixed(0);
    return `Content size must not exceed ${maxSizeInMiB} MiB (${MAX_CONTENT_SIZE_BYTES.toLocaleString()} bytes)`;
  }
}

export function MaxContentSize(validationOptions?: ValidationOptions) {
  return function (object: object, propertyName: string) {
    registerDecorator({
      target: object.constructor,
      propertyName: propertyName,
      options: validationOptions,
      constraints: [],
      validator: MaxContentSizeConstraint,
    });
  };
}

// Utility function to get content size in bytes
export function getContentSizeInBytes(content: string): number {
  if (!content) return 0;
  // Use Buffer for Node.js compatibility, fallback to Blob for browser
  if (typeof Buffer !== 'undefined') {
    return Buffer.byteLength(content, 'utf8');
  } else if (typeof Blob !== 'undefined') {
    return new Blob([content]).size;
  } else {
    // Fallback to character count * 3 (conservative UTF-8 estimate)
    return content.length * 3;
  }
}

// Utility function to format content size for display
export function formatContentSize(sizeInBytes: number): string {
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} bytes`;
  } else if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(1)} KiB`;
  } else {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MiB`;
  }
}

// Utility function to check if content exceeds the limit
export function isContentSizeValid(content: string): boolean {
  return getContentSizeInBytes(content) <= MAX_CONTENT_SIZE_BYTES;
}
